/**
 * Global Data Prefetch Hook
 *
 * Background prefetch of ALL offline data after login
 * - Non-blocking: Runs in background while user navigates
 * - Resume/Recovery: Saves progress, resumes on page refresh/tab reopen
 * - Smart Caching: Only prefetches when cache stale (4+ hours)
 * - Covers 9 entity types for complete offline access
 */

import { useState, useEffect, useRef, useCallback } from 'react';
import {
  offlineCattleApi,
  offlineHealthRecordApi,
  offlineMilkProductionApi,
  offlineMedicineApi,
  offlineInventoryApi,
  offlineShedApi,
  offlineFoodHistoryApi,
  offlineRfidScanApi,
  offlineDungTransactionApi,
} from '../lib/offline';
import { offlineMasterDataApi } from '../lib/offline/offlineMasterData';

// Entity types to prefetch
const ENTITY_TYPES = [
  'cattle',
  'shed',
  'healthRecord',
  'milkProduction',
  'medicine',
  'inventory',
  'foodHistory',
  'rfidScan',
  'dungTransaction',
] as const;

// LocalStorage keys
const PREFETCH_TIMESTAMP_KEY = 'global_prefetch_timestamp';
const PREFETCH_PROGRESS_KEY = 'global_prefetch_progress';
const PREFETCH_TTL = 4 * 60 * 60 * 1000; // 4 hours
const RESUME_WINDOW = 30 * 60 * 1000; // 30 minutes

// Progress tracking interface
interface PrefetchProgress {
  completedEntities: string[];
  lastAttemptedEntity?: string;
  startedAt: number;
  lastAttemptedAt?: number;
}

// API list for mapping
const ENTITY_APIS = {
  cattle: offlineCattleApi,
  shed: offlineShedApi,
  healthRecord: offlineHealthRecordApi,
  milkProduction: offlineMilkProductionApi,
  medicine: offlineMedicineApi,
  inventory: offlineInventoryApi,
  foodHistory: offlineFoodHistoryApi,
  rfidScan: offlineRfidScanApi,
  dungTransaction: offlineDungTransactionApi,
} as const;

interface UseGlobalDataPrefetchReturn {
  isLoading: boolean;
  progress: number; // 0-100
  currentTask: string;
  error: string | null;
  isComplete: boolean;
  startBackgroundPrefetch: () => void;
  needsPrefetch: () => boolean;
  forcePrefetch: () => void;
  /**
   * Set to true to block component rendering until prefetch completes
   * Used to prevent dashboard access before all data is cached
   */
  isBlocking: boolean;
}

/**
 * Check if prefetch is needed based on cache age
 */
function shouldPrefetch(): boolean {
  if (!navigator.onLine) {
    console.log('[GlobalPrefetch] Offline - skipping prefetch');
    return false;
  }

  const timestamp = localStorage.getItem(PREFETCH_TIMESTAMP_KEY);
  if (!timestamp) {
    console.log('[GlobalPrefetch] No cache timestamp - prefetch needed');
    return true;
  }

  const age = Date.now() - parseInt(timestamp);
  const isStale = age >= PREFETCH_TTL;

  if (!isStale) {
    console.log(`[GlobalPrefetch] Cache fresh (${Math.round(age / 60000)} min old) - skipping prefetch`);
  }

  return isStale;
}

/**
 * Get prefetch progress from localStorage
 */
function getProgress(): PrefetchProgress | null {
  const progress = localStorage.getItem(PREFETCH_PROGRESS_KEY);
  if (!progress) return null;

  try {
    const parsed = JSON.parse(progress);
    // Check if progress is stale (> 30 minutes)
    if (Date.now() - parsed.startedAt > RESUME_WINDOW) {
      console.log('[GlobalPrefetch] Progress stale (> 30 min) - starting fresh');
      return null;
    }
    return parsed;
  } catch (error) {
    console.error('[GlobalPrefetch] Error parsing progress:', error);
    return null;
  }
}

/**
 * Save progress to localStorage
 */
function saveProgress(progress: PrefetchProgress): void {
  localStorage.setItem(PREFETCH_PROGRESS_KEY, JSON.stringify(progress));
}

/**
 * Clear progress from localStorage
 */
function clearProgress(): void {
  localStorage.removeItem(PREFETCH_PROGRESS_KEY);
}

/**
 * Main hook for global data prefetch
 */
export function useGlobalDataPrefetch(): UseGlobalDataPrefetchReturn {
  const [isLoading, setIsLoading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [currentTask, setCurrentTask] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [isComplete, setIsComplete] = useState(false);
  // Synchronously check cache so we never start in a "blocking" state if cache is fresh
  const [isBlocking, setIsBlocking] = useState(() => shouldPrefetch());

  const prefetchRunningRef = useRef(false);

  /**
   * Prefetch all entities with progress tracking
   */
  const prefetchAllEntities = useCallback(async (entitiesToFetch?: string[]) => {
    if (prefetchRunningRef.current) {
      console.log('[GlobalPrefetch] Prefetch already running');
      return;
    }

    // Verify JWT token is available before starting prefetch
    const token = localStorage.getItem('saubhagya_jwt_token');
    if (!token) {
      console.error('[GlobalPrefetch] ✗ No JWT token found - cannot prefetch without authentication');
      setError('Authentication required for prefetch');
      return;
    }

    prefetchRunningRef.current = true;
    setIsLoading(true);
    setError(null);
    setIsComplete(false);

    try {
      const entitiesToProcess = entitiesToFetch || ENTITY_TYPES;
      const totalEntities = entitiesToProcess.length + 1; // +1 for master data
      let currentProgress = 0;

      // Initialize or resume progress
      let prefetchProgress = entitiesToFetch ? null : getProgress();
      if (!prefetchProgress) {
        prefetchProgress = {
          completedEntities: [],
          startedAt: Date.now(),
        };
        saveProgress(prefetchProgress);
        console.log('[GlobalPrefetch] Starting fresh prefetch');
      } else {
        console.log(
          `[GlobalPrefetch] Resuming prefetch - already fetched ${prefetchProgress.completedEntities.length} entities`
        );
      }

      // Step 1: Prefetch master data (0-20%)
      const masterDataProgress = Math.round((1 / totalEntities) * 100);
      setCurrentTask('Loading master data (breeds, species, colors)...');
      setProgress(0);

      try {
        await offlineMasterDataApi.prefetchAll();
        console.log('[GlobalPrefetch] Master data prefetched');
        currentProgress = masterDataProgress;
        setProgress(currentProgress);
      } catch (masterErr) {
        console.error('[GlobalPrefetch] Error prefetching master data:', masterErr);
        // Continue even if master data fails
      }

      // Step 2-9: Prefetch entities sequentially
      for (let i = 0; i < entitiesToProcess.length; i++) {
        if (!navigator.onLine) {
          console.log('[GlobalPrefetch] Went offline - pausing prefetch');
          setError('Went offline - will resume when online');
          break;
        }

        const entityType = entitiesToProcess[i] as typeof ENTITY_TYPES[number];

        // Skip if already completed
        if (prefetchProgress.completedEntities.includes(entityType)) {
          console.log(`[GlobalPrefetch] Skipping ${entityType} (already cached)`);
          currentProgress = masterDataProgress + Math.round(((i + 1) / totalEntities) * 100);
          setProgress(currentProgress);
          continue;
        }

        prefetchProgress.lastAttemptedEntity = entityType;
        prefetchProgress.lastAttemptedAt = Date.now();
        saveProgress(prefetchProgress);

        setCurrentTask(`Loading ${entityType}...`);

        try {
          const api = ENTITY_APIS[entityType];
          console.log(`[GlobalPrefetch] Fetching ${entityType}...`);

          const cachedData = await api.getAll(0, 1000);

          // Verify data was actually fetched
          if (!cachedData || cachedData.length === 0) {
            console.warn(`[GlobalPrefetch] ⚠ ${entityType} returned empty data (${cachedData ? cachedData.length : 0} records)`);
          }

          prefetchProgress.completedEntities.push(entityType);
          saveProgress(prefetchProgress);

          console.log(`[GlobalPrefetch] ✓ ${entityType} cached (${cachedData ? cachedData.length : 0} records)`);

          currentProgress = masterDataProgress + Math.round(((i + 1) / totalEntities) * 100);
          setProgress(currentProgress);
        } catch (entityErr) {
          console.error(`[GlobalPrefetch] ✗ Error fetching ${entityType}:`, entityErr);
          // Continue with next entity even if this one fails
          setError(`Failed to cache ${entityType}: ${entityErr instanceof Error ? entityErr.message : String(entityErr)}`);
        }
      }

      // Completion check
      const allCompleted = ENTITY_TYPES.every(e => prefetchProgress.completedEntities.includes(e));

      // Log summary of what was cached
      console.log('[GlobalPrefetch] Completed entities:', prefetchProgress.completedEntities);
      console.log('[GlobalPrefetch] Prefetch summary:', {
        completed: prefetchProgress.completedEntities.length,
        total: ENTITY_TYPES.length,
        entities: prefetchProgress.completedEntities,
        missing: ENTITY_TYPES.filter(e => !prefetchProgress.completedEntities.includes(e))
      });

      if (allCompleted) {
        console.log('[GlobalPrefetch] ✓ All entities cached - prefetch complete');
        clearProgress();
        localStorage.setItem(PREFETCH_TIMESTAMP_KEY, Date.now().toString());
        setProgress(100);
        setCurrentTask('Ready for offline!');
        setIsComplete(true);
        setError(null);
        setIsBlocking(false); // Unblock dashboard - all data cached
      } else {
        const cached = prefetchProgress.completedEntities.length;
        const total = ENTITY_TYPES.length;
        console.log(
          `[GlobalPrefetch] ⚠ Partial prefetch: ${cached}/${total} entities cached. Missing: ${ENTITY_TYPES.filter(e => !prefetchProgress.completedEntities.includes(e)).join(', ')}`
        );
        setProgress(100);
        setCurrentTask(`Cached ${cached}/${total} - will continue on next sync`);
        setIsComplete(false);
        setIsBlocking(false); // Unblock dashboard even with partial cache
      }
    } catch (err) {
      const errorMsg = err instanceof Error ? err.message : String(err);
      console.error('[GlobalPrefetch] Prefetch error:', errorMsg);
      setError(errorMsg);
      setIsComplete(false);
    } finally {
      prefetchRunningRef.current = false;
      setIsLoading(false);
    }
  }, []);

  /**
   * Start background prefetch
   */
  const startBackgroundPrefetch = useCallback(() => {
    console.log('[GlobalPrefetch] Starting background prefetch...');

    if (!shouldPrefetch()) {
      console.log('[GlobalPrefetch] Prefetch not needed');
      return;
    }

    // Run in background (no await)
    prefetchAllEntities();
  }, [prefetchAllEntities]);

  /**
   * Force prefetch (ignores cache validation)
   */
  const forcePrefetch = useCallback(() => {
    console.log('[GlobalPrefetch] Force refreshing all data...');
    clearProgress();
    localStorage.removeItem(PREFETCH_TIMESTAMP_KEY);
    prefetchAllEntities();
  }, [prefetchAllEntities]);

  /**
   * Check if prefetch is needed (for external use)
   */
  const needsPrefetch = useCallback((): boolean => {
    return shouldPrefetch();
  }, []);

  /**
   * Handle cache fresh state without auto-starting prefetch
   * Prefetch is triggered by GlobalPrefetchProvider after user logs in
   */
  useEffect(() => {
    // Check if cache is fresh (prefetch not needed)
    if (!shouldPrefetch()) {
      // Cache is fresh - unblock dashboard immediately
      console.log('[GlobalPrefetch] Cache is fresh - using cached data, unblocking dashboard');
      setIsBlocking(false);
      setIsComplete(true);
      setProgress(100);
      setCurrentTask('Using cached data');
    }
  }, []);

  return {
    isLoading,
    progress,
    currentTask,
    error,
    isComplete,
    isBlocking,
    startBackgroundPrefetch,
    needsPrefetch,
    forcePrefetch,
  };
}
